import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi, userApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/components/ui/toast';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { addToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      addToast({
        title: '请输入用户名和密码',
        variant: 'warning',
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.login(username, password);
      const { access } = response.data;

      // Get user profile
      // Set token temporarily so the API interceptor can add Authorization header
      useAuthStore.setState({ token: access });

      const userResponse = await userApi.getProfile();
      const user = userResponse.data.data?.user || userResponse.data.data || userResponse.data;

      setAuth(user, access);
      
      addToast({
        title: '登录成功',
        description: '欢迎回来！',
        variant: 'success',
      });
      
      navigate('/dashboard');
    } catch (error: any) {
      const message = error.response?.data?.detail || '登录失败，请检查用户名和密码';
      addToast({
        title: '登录失败',
        description: message,
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">登录账号</h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="username">用户名</Label>
          <Input
            id="username"
            type="text"
            placeholder="请输入用户名"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">密码</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="请输入密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              登录中...
            </>
          ) : (
            '登录'
          )}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600 mt-6">
        还没有账号？{' '}
        <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
          立即注册
        </Link>
      </p>
    </div>
  );
}
